import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import ChevronRight from 'shared/components/vxl/icon/icons/ChevronRight';
import Illustration, {
  IllustrationSourceType,
} from 'shared/components/vxl/Illustration/Illustration';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import theme from 'shared/styles/theme';

export interface OnboardingCardProps {
  /** Text to describe the purpose of the card */
  bodyText: string;
  /** Text that describe what action clicking on the card triggers */
  actionText?: string;
  /** Illustration that appear of the right of the card */
  illustration?: IllustrationSourceType;
  /** Boolean to disable the card */
  isDisabled?: boolean;
  /** Action to be perfomed when the card is clicked */
  onPress: () => void;
  /** Boolean to show border around the card */
  showBorder?: boolean;
}

export const OnboardingCard = ({
  bodyText,
  actionText,
  illustration,
  isDisabled = false,
  onPress,
  showBorder = false,
}: OnboardingCardProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  return (
    <Pressable
      style={[
        styles.cardContainer,
        showBorder && styles.border,
        isFocused && styles.focus,
        isHovered && styles.hover,
        isPressed && styles.press,
      ]}
      onHoverIn={() => {
        setIsHovered(true);
      }}
      onHoverOut={() => {
        setIsHovered(false);
      }}
      onBlur={() => {
        setIsFocused(false);
      }}
      onFocus={() => {
        setIsFocused(true);
      }}
      onPressIn={() => {
        setIsPressed(true);
      }}
      onPressOut={() => {
        setIsPressed(false);
      }}
      onPress={onPress}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      role="button"
    >
      <View style={styles.textContainer}>
        <BodyBaseText
          style={isDisabled && styles.disabledText}
          numberOfLines={3}
        >
          {bodyText}
        </BodyBaseText>
        <View style={styles.actionTextContainer}>
          <BodySmBoldText
            style={isDisabled ? styles.disabledText : styles.actionText}
            numberOfLines={1}
          >
            {actionText}
          </BodySmBoldText>
          <ChevronRight
            iconKey="CHEVRON_RIGHT"
            iconColor={
              isDisabled
                ? theme.color.text.muted
                : theme.color.interactive.primary
            }
            iconSize={theme.spacing.m}
          />
        </View>
      </View>
      <View style={styles.illustration}>
        <Illustration illustration={illustration ?? 'PLACEHOLDER_A'} />
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  actionText: {
    color: theme.color.interactive.primary,
  },
  actionTextContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: theme.spacing.s,
    textAlignVertical: 'bottom',
    verticalAlign: 'middle',
  },
  border: {
    borderColor: theme.color.background.secondary,
    borderWidth: 1,
  },
  cardContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.interactive.secondary,
    borderRadius: 4,
    flexBasis: 'auto',
    flexDirection: 'row',
    minHeight: 126,
    overflow: 'hidden',
  },
  disabledText: {
    color: theme.color.interactive.disabled.text,
  },
  focus: {
    outlineColor: theme.color.interactive.focused.border,
  },
  hover: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  illustration: {
    height: 70,
    marginRight: theme.spacing.s,
    minWidth: 0,
    overflow: 'hidden',
    width: 79,
  },
  press: {
    backgroundColor: theme.color.interactive.secondaryPressed,
  },
  textContainer: {
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'center',
    margin: theme.spacing.m,
  },
});

export default OnboardingCard;
